import { Component, type ErrorInfo, type ReactNode } from "react";
import posthog from "posthog-js";

import type { BasicSuccessResponse } from "@/types/responses";

type State = {
  hasError: boolean;
  errorMessage: string;
};

type Props = {
  componentName: string;
  sendError?: (data: ErrorData) => Promise<BasicSuccessResponse>;
  children: ReactNode;
};

export type ErrorData = {
  component: string;
  msg: string;
  url: string;
};

const contactUs = () => window.open("mailto:niamh@tigim.co");

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    errorMessage: ""
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, errorMessage: error.toString() };
  }

  public async componentDidCatch(error: Error, _errorInfo: ErrorInfo): Promise<void> {
    this.setState({ hasError: true, errorMessage: error.toString() });
    const errorData: ErrorData = {
      url: window.location.href,
      msg: error.toString(),
      component: this.props.componentName
    };
    error.message = `ErrorBoundary => ${error.message} \n Component Stack: ${_errorInfo.componentStack}`;
    console.debug(`ErrorBoundary => ${error.toString()}`, errorData);
    posthog.captureException(error, { ...errorData, boundary: true });
  }

  public render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <main>
        <img src="/images/logos/error.svg" alt="Error page" />
        <div>
          <p>We are sorry, an error has occured!</p>
          <p className="try">{getMessage(this.state.errorMessage)}</p>
          <p>If the error continues, please contact us.</p>
          <button className="contactUs" type="button" aria-label="Contact Us" onClick={contactUs}>
            Contact Support
          </button>
        </div>
      </main>
    );
  }
}

export default ErrorBoundary;

const getMessage = (errorMessage: string) =>
  errorMessage.toString().includes("ChunkLoadError")
    ? "Please refresh the page using the keys: Ctrl + Shift + R"
    : "Please try again";
